import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../layouts/AppLayout.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import NavSocialMedia from "../components/NavSocialMedia.jsx";
import CaptionCard from "../components/CaptionCard.jsx";
import ImageCard from "../components/ImageCard.jsx";
import VideoCard from "../components/VideoCard.jsx";
import TemplateCard from "../components/TemplateCard.jsx";
import ReelCard from "../components/ReelCard.jsx";
import TemplateConfirmationModal from "../components/TemplateConfirmationModal.jsx";
import API from "../config/api.js";

export default function AllBonuses() {
  const { subscription } = useAuth();

  const [captions, setCaptions]   = useState([]);
  const [images, setImages]       = useState([]);
  const [videos, setVideos]       = useState([]);
  const [templates, setTemplates] = useState([]);
  const [reels, setReels]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [playingReelId, setPlayingReelId] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Calculate maxContent based on subscription age (months since created)
  const maxContent = useMemo(() => {
    if (!subscription?.createdat) return 1;
    const created = new Date(subscription.createdat);
    const now     = new Date();
    const months  = (now.getFullYear() - created.getFullYear()) * 12
                  + (now.getMonth() - created.getMonth());
    return months + 1;
  }, [subscription]);

  const fetchList = useCallback(async (url) => {
    const fd = new FormData();
    fd.append("currentPage", 1);
    fd.append("gamification", "true");
    const res = await fetch(url, { method: "POST", credentials: "include", body: fd });
    return res.json();
  }, []);

  const fetchBonuses = useCallback(async () => {
    setLoading(true);
    try {
      const [imagesRes, captionsRes, templatesRes, videosRes, reelsRes] = await Promise.all([
        fetchList(`${API.images}?list&gamification`),
        fetchList(`${API.captions}?list&gamification`),
        fetchList(`${API.templates}?list&gamification`),
        fetchList(`${API.videos}?list&gamification`),
        fetchList(`${API.reels}?list&gamification`),
      ]);
      setImages(Array.isArray(imagesRes)     ? imagesRes     : []);
      setCaptions(Array.isArray(captionsRes) ? captionsRes   : []);
      setTemplates(Array.isArray(templatesRes) ? templatesRes : []);
      setVideos(Array.isArray(videosRes)     ? videosRes     : []);
      setReels(Array.isArray(reelsRes)       ? reelsRes      : []);
    } catch (err) {
      console.error("AllBonuses fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchList]);

  useEffect(() => { fetchBonuses(); }, [fetchBonuses]);

  // Shuffle helper (Fisher-Yates)
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const allItems = useMemo(() => {
    const combined = [
      ...captions.map((d)  => ({ type: "caption",  key: `caption-${d.idcaption}`,   data: d })),
      ...images.map((d)    => ({ type: "image",     key: `image-${d.idimage}`,       data: d })),
      ...videos.map((d)    => ({ type: "video",     key: `video-${d.idvideo}`,       data: d })),
      ...templates.map((d) => ({ type: "template",  key: `template-${d.idtemplate}`, data: d })),
      ...reels.map((d)     => ({ type: "reel",      key: `reel-${d.idreel}`,         data: d })),
    ];
    // Shuffle then limit to maxContent
    return shuffle(combined).slice(0, maxContent);
  }, [captions, images, videos, templates, reels, maxContent]);





  // Template handlers (same as MyFavorites)
  const applyTemplateToDraft = (template) => {
    window._draft_prompt_clear?.();
    const ids = Array.isArray(template.idimages)
      ? template.idimages
      : (template.idimages || "").split(",").map((id) => id.trim()).filter(Boolean);
    ids.forEach((id) => {
      window._draft_prompt_item_add?.("image", id, `upload/image/${id}/image/original.jpg`);
    });
    window._draft_prompt_open?.();
  };

  const handleUseTemplate = (template) => {
    if (!template) return;
    if (template.type === "images" || !template.url || template.url === "#") {
      applyTemplateToDraft(template);
    } else {
      setSelectedTemplate(template);
    }
  };



  return (
    <AppLayout currentPage="all-bonuses">
      <NavSocialMedia currentPage="all-bonuses" />



      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
        </div>
      ) : (
        <div className="row g-2">
          {allItems.length === 0 ? (
            <div className="col-12 text-center py-5 text-muted">No bonuses found.</div>
          ) : allItems.map((item) => (
            <div key={item.key} className="col-md-3">
              {item.type === "caption"  && <CaptionCard caption={item.data} />}
              {item.type === "image"    && <ImageCard image={item.data} />}
              {item.type === "video"    && (
                <VideoCard
                  video={item.data}
                  isPlaying={playingVideoId === (item.data.idvideo_md5 || item.data.idvideo)}
                  onTogglePreview={(id) => setPlayingVideoId((p) => p === id ? null : id)}
                  onVideoEnded={() => setPlayingVideoId(null)}
                />
              )}
              {item.type === "template" && <TemplateCard template={item.data} onUse={handleUseTemplate} />}
              {item.type === "reel"     && (
                <ReelCard
                  reel={item.data}
                  isPlaying={playingReelId === item.data.idreel}
                  onTogglePreview={(id) => setPlayingReelId((p) => p === id ? null : id)}
                  onVideoEnded={() => setPlayingReelId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <TemplateConfirmationModal
        isOpen={Boolean(selectedTemplate)}
        editUrl={selectedTemplate?.url || ""}
        onClose={() => setSelectedTemplate(null)}
        onContinue={() => { applyTemplateToDraft(selectedTemplate); setSelectedTemplate(null); }}
      />
    </AppLayout>
  );
}